import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { type RetailerDTO, isSmallKiranaOrExempt } from "@mydailydirect/api-client";
import { useMyDailyDirectClient } from "@mydailydirect/app-kit";
import {
  Button,
  useToast,
  compressAndReadImage,
  parseRetailerKyc,
  serializeRetailerKyc,
  SUPPORTED_LANGUAGES,
  Badge,
} from "@mydailydirect/ui";
import {
  Camera,
  CheckCircle2,
  FileText,
  Globe,
  Store,
  UploadCloud,
  X,
  Trash2,
  FileCheck,
  ShieldCheck,
  Utensils,
} from "lucide-react";

interface UploadShopProofModalProps {
  open: boolean;
  onClose: () => void;
  retailer: RetailerDTO | null;
  onSuccess?: () => void;
}

export function UploadShopProofModal({
  open,
  onClose,
  retailer,
  onSuccess,
}: UploadShopProofModalProps) {
  const client = useMyDailyDirectClient();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const shopCameraRef = useRef<HTMLInputElement>(null);
  const shopFileRef = useRef<HTMLInputElement>(null);
  const docPdfRef = useRef<HTMLInputElement>(null);
  const docFileRef = useRef<HTMLInputElement>(null);
  const fssaiPdfRef = useRef<HTMLInputElement>(null);
  const fssaiFileRef = useRef<HTMLInputElement>(null);

  const [shopPhoto, setShopPhoto] = useState<string>("");
  const [docUrl, setDocUrl] = useState<string>("");
  const [languagePreference, setLanguagePreference] = useState<string>("");
  const [gstNumber, setGstNumber] = useState<string>("");
  const [fssaiNumber, setFssaiNumber] = useState<string>("");
  const [fssaiCertificateUrl, setFssaiCertificateUrl] = useState<string>("");
  const [isCompressing, setIsCompressing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (retailer) {
      const parsed = parseRetailerKyc(retailer.photoUrl);
      setShopPhoto(parsed.shopPhoto || "");
      setDocUrl(parsed.docUrl || "");
      setLanguagePreference(parsed.languagePreference || "");
      setGstNumber(retailer.gstNumber || "");
      setFssaiNumber(retailer.fssaiNumber || "");
      setFssaiCertificateUrl(retailer.fssaiCertificateUrl || "");
    }
  }, [retailer, open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open || !retailer) return null;
  if (typeof document === "undefined") return null;

  const isSmallKirana = isSmallKiranaOrExempt(retailer);

  const isDocPdf =
    docUrl.startsWith("data:application/pdf") ||
    docUrl.toLowerCase().includes(".pdf");

  const isFssaiPdf =
    fssaiCertificateUrl.startsWith("data:application/pdf") ||
    fssaiCertificateUrl.toLowerCase().includes(".pdf");

  const handleShopPhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsCompressing(true);
    try {
      const dataUrl = await compressAndReadImage(file, 1200, 0.82);
      setShopPhoto(dataUrl);
      toast({
        title: "Storefront Photo Attached! 📸",
        description: "Physical storefront/signboard image saved.",
        variant: "success",
      });
    } catch (err) {
      toast({
        title: "Upload Error",
        description: err instanceof Error ? err.message : "Could not process photo",
        variant: "error",
      });
    } finally {
      setIsCompressing(false);
      if (e.target) e.target.value = "";
    }
  };

  const handleDocChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsCompressing(true);
    try {
      const dataUrl = await compressAndReadImage(file, 1200, 0.82);
      setDocUrl(dataUrl);
      const isDoc = file.type.includes("pdf") || file.name.toLowerCase().endsWith(".pdf");
      toast({
        title: isDoc ? "KYC Document Attached! 📄" : "Certificate Attached! 📑",
        description: `${file.name} ready to submit for KYC review.`,
        variant: "success",
      });
    } catch (err) {
      toast({
        title: "Upload Error",
        description: err instanceof Error ? err.message : "Could not process document",
        variant: "error",
      });
    } finally {
      setIsCompressing(false);
      if (e.target) e.target.value = "";
    }
  };

  const handleFssaiDocChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsCompressing(true);
    try {
      const dataUrl = await compressAndReadImage(file, 1200, 0.82);
      setFssaiCertificateUrl(dataUrl);
      toast({
        title: "FSSAI Certificate Attached! 🥗",
        description: `${file.name} saved for food safety verification.`,
        variant: "success",
      });
    } catch (err) {
      toast({
        title: "Upload Error",
        description: err instanceof Error ? err.message : "Could not process document",
        variant: "error",
      });
    } finally {
      setIsCompressing(false);
      if (e.target) e.target.value = "";
    }
  };

  const handleSave = async () => {
    const cleanGst = gstNumber.trim().toUpperCase();
    const cleanFssai = fssaiNumber.trim();

    // Standard B2B buyers require either GSTIN or FSSAI
    if (!isSmallKirana && (!cleanGst || cleanGst.length < 15) && (!cleanFssai || cleanFssai.length < 14)) {
      toast({
        title: "GSTIN or FSSAI License Required",
        description: "Wholesale compliance requires a valid 15-character GSTIN or 14-digit FSSAI number.",
        variant: "error",
      });
      return;
    }

    if (!shopPhoto && !docUrl && !fssaiCertificateUrl && !isSmallKirana) {
      toast({
        title: "Documents Required",
        description: "Please upload your shopfront photo or attach your GST / FSSAI certificate.",
        variant: "error",
      });
      return;
    }

    const serializedPayload = serializeRetailerKyc({
      shopPhoto,
      docUrl,
      languagePreference,
    });

    // Upgrading from Small Kirana to Standard wholesale tier if GSTIN is provided
    const isUpgradedToGst = cleanGst.length >= 15;
    const complianceTier = isUpgradedToGst
      ? "GST_REGISTERED"
      : retailer.complianceTier || (isSmallKirana ? "SMALL_KIRANA" : "GST_REGISTERED");
    const gstExempt = isUpgradedToGst ? false : (isSmallKirana ? true : false);

    setIsSaving(true);
    try {
      let targetId = retailer.id;
      if (!targetId || targetId === 0) {
        const created = await client.retailers.create({
          id: null,
          name: retailer.name || "Retail Buyer",
          phone: retailer.phone,
          city: retailer.city || "Bengaluru",
          addressLine: retailer.addressLine || "Bengaluru",
          photoUrl: serializedPayload,
          gstNumber: cleanGst || null,
          fssaiNumber: cleanFssai || null,
          fssaiCertificateUrl: fssaiCertificateUrl || null,
          complianceTier,
          gstExempt,
          status: isSmallKirana ? "APPROVED" : "PENDING",
          onboardingSource: "SELF_SIGNUP",
          onboardedBy: "Self (Catalog Web/App)",
        } as any);
        targetId = created.id!;
      } else {
        await client.retailers.update(targetId, {
          ...retailer,
          photoUrl: serializedPayload,
          gstNumber: cleanGst || null,
          fssaiNumber: cleanFssai || null,
          fssaiCertificateUrl: fssaiCertificateUrl || null,
          complianceTier,
          gstExempt,
          status: retailer.status === "APPROVED" ? "APPROVED" : "PENDING",
        });
      }

      await queryClient.invalidateQueries({ queryKey: ["retailers"] });
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({
        title: isUpgradedToGst ? "Upgraded to GST Wholesale! 🏛️" : "KYC Documents Saved! 📋",
        description: isUpgradedToGst
          ? "Your 15-digit GSTIN has been saved and your account is upgraded to wholesale registered."
          : "Your shop details and compliance documents have been submitted.",
        variant: "success",
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      toast({
        title: "Upload Failed",
        description: err instanceof Error ? err.message : "Failed to upload shop proof",
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isSaveDisabled =
    isSaving ||
    isCompressing ||
    (!isSmallKirana &&
      (!gstNumber || gstNumber.trim().length < 15) &&
      (!fssaiNumber || fssaiNumber.trim().length < 14));

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm animate-in fade-in"
      style={{ zIndex: 10000 }}
      onClick={onClose}
    >
      <div
        className="relative z-10 w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl overflow-hidden p-4 sm:p-6 space-y-4 animate-in zoom-in-95 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <Store className="h-5 w-5 text-primary" />
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-foreground">Update KYC, GSTIN & FSSAI</h3>
                {isSmallKirana ? (
                  <Badge variant="warning" className="text-[10px] px-2 py-0">Small Kirana (Exempt)</Badge>
                ) : (
                  <Badge variant="success" className="text-[10px] px-2 py-0">Wholesale Registered</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{retailer.name || "Retail Buyer"} · {retailer.phone || "No phone"}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 rounded-full"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Small Kirana Notice */}
        {isSmallKirana && (
          <div className="p-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-xs text-emerald-900 dark:text-emerald-200 flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <p className="font-bold">Small Kirana Mode Active</p>
              <p className="text-2xs text-muted-foreground">
                Your shop is exempt from mandatory GSTIN registration. You can order wholesale staples anytime. If you obtain a <strong>GSTIN</strong> or <strong>FSSAI Certificate</strong>, add it below.
              </p>
            </div>
          </div>
        )}

        {/* 1. STOREFRONT PHOTO SECTION */}
        <div className="space-y-2 border border-border rounded-xl p-3 bg-card">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-foreground flex items-center gap-1.5">
              <Camera className="h-4 w-4 text-primary" />
              1. Storefront / Signboard Photo
            </label>
            {shopPhoto ? (
              <span className="text-2xs font-bold text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Photo Attached
              </span>
            ) : (
              <span className="text-2xs text-muted-foreground font-medium">Physical Shop Front</span>
            )}
          </div>

          <input
            type="file"
            ref={shopCameraRef}
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleShopPhotoChange}
          />
          <input
            type="file"
            ref={shopFileRef}
            accept="image/*"
            className="hidden"
            onChange={handleShopPhotoChange}
          />

          {shopPhoto ? (
            <div className="relative rounded-xl border border-border overflow-hidden h-36 bg-muted flex items-center justify-center">
              <img src={shopPhoto} alt="Shop Front" className="h-full w-full object-cover" />
              <div className="absolute bottom-2 right-2 flex gap-1.5">
                <button
                  type="button"
                  onClick={() => shopCameraRef.current?.click()}
                  className="px-2.5 py-1 bg-black/75 hover:bg-black text-white text-xs font-semibold rounded-lg shadow-xs flex items-center gap-1"
                >
                  <Camera className="h-3.5 w-3.5" /> Retake
                </button>
                <button
                  type="button"
                  onClick={() => shopFileRef.current?.click()}
                  className="px-2.5 py-1 bg-black/75 hover:bg-black text-white text-xs font-semibold rounded-lg shadow-xs flex items-center gap-1"
                >
                  <UploadCloud className="h-3.5 w-3.5" /> Change
                </button>
                <button
                  type="button"
                  onClick={() => setShopPhoto("")}
                  className="px-2 py-1 bg-destructive/80 hover:bg-destructive text-white text-xs font-semibold rounded-lg shadow-xs flex items-center"
                  title="Remove photo"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-border rounded-xl p-3 flex items-center justify-between gap-3 bg-muted/20">
              <div className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground block">Capture shop storefront</span>
                Signboard, street view, or entrance
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={() => shopCameraRef.current?.click()}
                  disabled={isCompressing}
                  className="text-xs gap-1 font-bold h-8"
                >
                  <Camera className="h-3.5 w-3.5" /> Camera
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => shopFileRef.current?.click()}
                  disabled={isCompressing}
                  className="text-xs gap-1 font-medium h-8"
                >
                  <UploadCloud className="h-3.5 w-3.5" /> Gallery
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* 2. GSTIN NUMBER SECTION */}
        <div className="space-y-1.5 border border-border rounded-xl p-3 bg-card">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-foreground flex items-center gap-1.5">
              <FileCheck className="h-4 w-4 text-primary" />
              2. GSTIN / Trade Registration No.
              {!isSmallKirana && <span className="text-destructive">*</span>}
            </label>
            {gstNumber.trim().length >= 15 ? (
              <span className="text-2xs font-bold text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Valid 15-Digit GSTIN
              </span>
            ) : isSmallKirana ? (
              <span className="text-2xs text-muted-foreground font-medium">Optional for Kirana (&lt; ₹40L)</span>
            ) : (
              <span className="text-2xs text-destructive font-semibold">15 Characters Required</span>
            )}
          </div>
          <input
            type="text"
            value={gstNumber}
            onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
            placeholder={isSmallKirana ? "Optional (enter to upgrade to GST Wholesale)" : "e.g. 29AAAAA0000A1Z5"}
            maxLength={20}
            className="w-full px-3 py-2 text-xs font-mono font-bold rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40 uppercase"
          />
          <p className="text-2xs text-muted-foreground">
            Must match your official GST certificate or government trade registration.
          </p>
        </div>

        {/* 3. FSSAI FOOD SAFETY LICENSE SECTION */}
        <div className="space-y-2 border border-border rounded-xl p-3 bg-card">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-foreground flex items-center gap-1.5">
              <Utensils className="h-4 w-4 text-emerald-600" />
              3. FSSAI License / Registration No. (14-Digit)
            </label>
            {fssaiNumber.trim().length >= 14 ? (
              <span className="text-2xs font-bold text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> 14-Digit FSSAI
              </span>
            ) : (
              <span className="text-2xs text-muted-foreground font-medium">Food Safety Proof</span>
            )}
          </div>
          <input
            type="text"
            value={fssaiNumber}
            onChange={(e) => setFssaiNumber(e.target.value.replace(/\D/g, "").slice(0, 14))}
            placeholder="e.g. 11223344556677 (14 digits)"
            maxLength={14}
            className="w-full px-3 py-2 text-xs font-mono font-bold rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <p className="text-2xs text-muted-foreground">
            Food safety license/registration issued by Food Safety and Standards Authority of India.
          </p>

          <input
            type="file"
            ref={fssaiPdfRef}
            accept="application/pdf"
            className="hidden"
            onChange={handleFssaiDocChange}
          />
          <input
            type="file"
            ref={fssaiFileRef}
            accept="image/*,application/pdf"
            className="hidden"
            onChange={handleFssaiDocChange}
          />

          {fssaiCertificateUrl ? (
            isFssaiPdf ? (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-foreground">FSSAI Certificate (PDF) Attached</p>
                    <p className="text-2xs text-muted-foreground">Official food safety license certificate</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => fssaiPdfRef.current?.click()}
                    className="h-8 text-xs gap-1 font-semibold"
                  >
                    Replace
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setFssaiCertificateUrl("")}
                    className="h-8 text-xs text-destructive hover:bg-destructive/10"
                    title="Remove document"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="relative rounded-xl border border-border overflow-hidden h-32 bg-muted flex items-center justify-center">
                <img src={fssaiCertificateUrl} alt="FSSAI Certificate" className="h-full w-full object-contain" />
                <div className="absolute bottom-2 right-2 flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => fssaiFileRef.current?.click()}
                    className="px-2.5 py-1 bg-black/75 hover:bg-black text-white text-xs font-semibold rounded-lg shadow-xs flex items-center gap-1"
                  >
                    <UploadCloud className="h-3.5 w-3.5" /> Replace
                  </button>
                  <button
                    type="button"
                    onClick={() => setFssaiCertificateUrl("")}
                    className="px-2 py-1 bg-destructive/80 hover:bg-destructive text-white text-xs font-semibold rounded-lg shadow-xs flex items-center"
                    title="Remove document"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )
          ) : (
            <div className="border border-dashed border-border rounded-xl p-2.5 flex items-center justify-between gap-2 bg-muted/20">
              <span className="text-2xs text-muted-foreground">Upload FSSAI License Document (PDF or Photo)</span>
              <div className="flex gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => fssaiPdfRef.current?.click()}
                  disabled={isCompressing}
                  className="text-2xs gap-1 h-7"
                >
                  <FileText className="h-3 w-3" /> PDF
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => fssaiFileRef.current?.click()}
                  disabled={isCompressing}
                  className="text-2xs gap-1 h-7"
                >
                  <UploadCloud className="h-3 w-3" /> Image
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* 4. SUPPORTING KYC DOCUMENT (PDF OR IMAGE) */}
        <div className="space-y-2 border border-border rounded-xl p-3 bg-card">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-foreground flex items-center gap-1.5">
              <FileText className="h-4 w-4 text-primary" />
              4. Supporting KYC Document (PDF / Image)
            </label>
            {docUrl ? (
              <span className="text-2xs font-bold text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Document Attached
              </span>
            ) : (
              <span className="text-2xs text-muted-foreground font-medium">GST Certificate / Trade License</span>
            )}
          </div>

          <input
            type="file"
            ref={docPdfRef}
            accept="application/pdf"
            className="hidden"
            onChange={handleDocChange}
          />
          <input
            type="file"
            ref={docFileRef}
            accept="image/*,application/pdf"
            className="hidden"
            onChange={handleDocChange}
          />

          {docUrl ? (
            isDocPdf ? (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-foreground">PDF Certificate Attached</p>
                    <p className="text-2xs text-muted-foreground">Official wholesale document ready to inspect</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => docPdfRef.current?.click()}
                    className="h-8 text-xs gap-1 font-semibold"
                  >
                    Replace
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setDocUrl("")}
                    className="h-8 text-xs text-destructive hover:bg-destructive/10"
                    title="Remove document"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="relative rounded-xl border border-border overflow-hidden h-36 bg-muted flex items-center justify-center">
                <img src={docUrl} alt="Document Proof" className="h-full w-full object-contain" />
                <div className="absolute bottom-2 right-2 flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => docFileRef.current?.click()}
                    className="px-2.5 py-1 bg-black/75 hover:bg-black text-white text-xs font-semibold rounded-lg shadow-xs flex items-center gap-1"
                  >
                    <UploadCloud className="h-3.5 w-3.5" /> Replace Doc
                  </button>
                  <button
                    type="button"
                    onClick={() => setDocUrl("")}
                    className="px-2 py-1 bg-destructive/80 hover:bg-destructive text-white text-xs font-semibold rounded-lg shadow-xs flex items-center"
                    title="Remove document"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )
          ) : (
            <div className="border border-dashed border-border rounded-xl p-3 flex items-center justify-between gap-3 bg-muted/20">
              <div className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground block">Upload GST / Trade Certificate</span>
                Accepts PDF or scanned certificate photo
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={() => docPdfRef.current?.click()}
                  disabled={isCompressing}
                  className="text-xs gap-1 font-bold h-8"
                >
                  <FileText className="h-3.5 w-3.5" /> Upload PDF
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => docFileRef.current?.click()}
                  disabled={isCompressing}
                  className="text-xs gap-1 font-medium h-8"
                >
                  <UploadCloud className="h-3.5 w-3.5" /> Image File
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Preferred Language Section */}
        <div className="space-y-1.5 border border-border rounded-xl p-3 bg-card">
          <div className="flex items-center justify-between">
            <label htmlFor="kyc-language" className="text-xs font-bold text-foreground flex items-center gap-1.5">
              <Globe className="h-4 w-4 text-primary" />
              Preferred Language (WhatsApp & Calls)
            </label>
            {languagePreference ? (
              <button
                type="button"
                onClick={() => setLanguagePreference("")}
                className="text-2xs font-bold text-muted-foreground hover:text-destructive underline"
              >
                Clear
              </button>
            ) : (
              <span className="text-2xs text-amber-600 dark:text-amber-400 font-semibold">Not Set</span>
            )}
          </div>
          <select
            id="kyc-language"
            value={languagePreference}
            onChange={(e) => setLanguagePreference(e.target.value)}
            className="w-full h-9 rounded-lg border border-border bg-background px-3 text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <option value="">Not Set (Select Preferred Language)</option>
            {SUPPORTED_LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </select>
        </div>

        {/* Submit Actions */}
        <div className="sticky -bottom-4 sm:-bottom-6 z-20 bg-card flex gap-2.5 pt-3 pb-1 border-t border-border">
          <Button
            type="button"
            variant="outline"
            className="w-1/3 text-xs"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            className="w-2/3 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white min-h-[42px]"
            loading={isSaving || isCompressing}
            disabled={isSaveDisabled}
            onClick={handleSave}
          >
            Save & Update KYC
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
